import type { SupportedLanguage } from './index'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Example:
 *   about: { it: 'chi-siamo', en: 'about-us' }
 *   → /it/chi-siamo, /en/about-us
 */
export const localizedRoutes = {
  home: { it: '', en: '' },
  zambePong: { it: 'zambepong', en: 'zambepong' },
  zambePongTournament: { it: 'zambepong/:id', en: 'zambepong/:id' },
  zambePongMatch: { it: 'zambepong/:id/partita/:matchId', en: 'zambepong/:id/match/:matchId' },
  zambePongZambeBet: { it: 'zambepong/:id/zambebet', en: 'zambepong/:id/zambebet' },
  zambePongZambeBetMatch: { it: 'zambepong/:id/zambebet/partita/:matchId', en: 'zambepong/:id/zambebet/match/:matchId' },
  login: { it: 'login', en: 'login' },
  admin: { it: 'admin', en: 'admin' },
} as const

export type RouteKey = keyof typeof localizedRoutes

const fill = (pattern: string, params: Record<string, string> = {}) =>
  pattern.replace(/:(\w+)/g, (_, name: string) => encodeURIComponent(params[name] ?? ''))

/**
 * Get the localized path for a route, riempiendo eventuali `:param` nel pattern.
 */
export function getLocalizedPath(
  routeKey: RouteKey,
  lang: SupportedLanguage,
  params?: Record<string, string>
): string {
  const path = fill(localizedRoutes[routeKey][lang], params)
  return path ? `/${lang}/${path}` : `/${lang}`
}

/**
 * Find route key from a path segment
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}

/** Stesso percorso in un'altra lingua: riconosce il pattern della route (anche con parametri) e lo ritraduce;
 * `search` (es. `?tab=standings`) viene riappeso così com'è. */
export function translatePath(pathname: string, target: SupportedLanguage, search = ''): string {
  return translatePathname(pathname, target) + search
}

function translatePathname(pathname: string, target: SupportedLanguage): string {
  const [, lang, ...rest] = pathname.split('/')
  const current = rest.join('/')
  for (const routes of Object.values(localizedRoutes)) {
    const source = routes[lang as SupportedLanguage]
    if (source === undefined) continue
    const regex = new RegExp('^' + source.replace(/:(\w+)/g, '(?<$1>[^/]+)') + '$')
    const found = current.match(regex)
    if (found) return `/${target}/${fill(routes[target], found.groups ?? {})}`.replace(/\/$/, '')
  }
  return `/${target}${current ? `/${current}` : ''}`
}
